import { useEffect, useState, useCallback } from "react";
import { useAppContext } from "../../../2_ksbHub/context/appContext";
import askomaService from "../services";

export default function useAskomaState() {
  const { currentUser, setAskomaLoading } = useAppContext();

  /**
   * =========================================================
   * ✅ STATE
   * =========================================================
   */
  const [latestMeasurement, setLatestMeasurement] = useState(null);
  const [measurementHistory, setMeasurementHistory] = useState([]);

  /**
   * =========================================================
   * ✅ LOADERS
   * =========================================================
   */
  const loadLatestMeasurement = useCallback(async () => {
    const data = await askomaService.getLatestMeasurement();

    setLatestMeasurement(data.measurement ?? null);
  }, []);

  const loadMeasurementHistory = useCallback(async (limit = null) => {
    const data = await askomaService.getMeasurementHistory(limit);

    setMeasurementHistory(data.measurements ?? []);
  }, []);

  /**
   * =========================================================
   * ✅ INITIAL LOAD
   * =========================================================
   */
  useEffect(() => {
    if (!currentUser?.id) return;

    const loadData = async () => {
      setAskomaLoading(true);

      if (currentUser.id === 205) {
        try {
          await Promise.all([
            loadLatestMeasurement(),
            loadMeasurementHistory(),
          ]);
        } finally {
          setAskomaLoading(false);
        }
      }
    };

    loadData();
  }, [
    currentUser?.id,
    loadLatestMeasurement,
    loadMeasurementHistory,
    setAskomaLoading,
  ]);

  /**
   * =========================================================
   * ✅ LIVE UPDATE (1 MINUTE)
   * =========================================================
   */
  useEffect(() => {
    if (!currentUser?.id) return;

    const interval = setInterval(async () => {
      try {
        const data = await askomaService.getLatestMeasurement();

        const measurement = data.measurement;

        if (!measurement) {
          return;
        }
        setLatestMeasurement((prev) => {
          if (prev?.id === measurement.id) {
            return prev;
          }

          return measurement;
        });

        setMeasurementHistory((prev) => {
          const exists = prev.some((item) => item.id === measurement.id);

          if (exists) {
            return prev;
          }

          return [measurement, ...prev];
        });
      } catch (err) {
        console.error("❌ Askoma live update failed:", err);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [currentUser?.id]);

  /**
   * =========================================================
   * ✅ EXPORT
   * =========================================================
   */
  return {
    latestMeasurement,
    measurementHistory,

    loadLatestMeasurement,
    loadMeasurementHistory,

    setLatestMeasurement,
    setMeasurementHistory,
  };
}
